import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation, Navigate, useNavigate, Link } from "react-router-dom";
import { BrandMark } from "@/components/BrandMark";
import UsePwaManifest from "@/hooks/usePwaManifest";
import {
  Home,
  CalendarDays,
  Wallet,
  User,
  Shirt,
  LogOut,
  Briefcase,
  Menu,
  Receipt,
  Edit3,
  X,
} from "lucide-react";
import { usePortalAuth } from "./PortalAuthContext";
import { LanguageSwitcher } from "./LanguageSwitcher";

const PRIMARY_TABS = [
  { to: "/dolgozo", labelKey: "nav.dashboard", icon: Home, end: true, testId: "tab-home" },
  { to: "/dolgozo/beosztas", labelKey: "nav.schedule", icon: CalendarDays, testId: "tab-schedule" },
  { to: "/dolgozo/penzugyek", labelKey: "nav.finances", icon: Wallet, testId: "tab-finances" },
  { to: "/dolgozo/profil", labelKey: "nav.profile", icon: User, testId: "tab-profile" },
];

const ALL_NAV = [
  { to: "/dolgozo", labelKey: "nav.dashboard", icon: Home, end: true, testId: "menu-home" },
  { to: "/dolgozo/beosztas", labelKey: "nav.schedule", icon: CalendarDays, testId: "menu-schedule" },
  { to: "/dolgozo/penzugyek", labelKey: "nav.finances", icon: Wallet, testId: "menu-finances" },
  { to: "/dolgozo/napi-zaras", labelKey: "nav.daily_closing", icon: Receipt, testId: "menu-daily-closing" },
  { to: "/dolgozo/tervezet", labelKey: "nav.drafts", icon: Edit3, testId: "menu-drafts" },
  { to: "/dolgozo/munkaruha", labelKey: "nav.workwear", icon: Shirt, testId: "menu-workwear" },
  { to: "/dolgozo/munkaviszony", labelKey: "nav.employment", icon: Briefcase, testId: "menu-employment" },
  { to: "/dolgozo/profil", labelKey: "nav.profile", icon: User, testId: "menu-profile" },
];

export function PortalShell() {
  const { employee, loading, logout } = usePortalAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  // iOS PWA fix: while the menu drawer is open, force body bg to white (or dark zinc-950 in
  // dark mode). Without this, the body's zinc-50 bg bleeds through the iOS safe-area zone and
  // shows a darker "csúnya szürke sáv" next to the white drawer.
  useEffect(() => {
    if (!menuOpen) return;
    const prevBody = document.body.style.backgroundColor;
    const prevHtml = document.documentElement.style.backgroundColor;
    const isDark = document.documentElement.classList.contains("dark");
    const target = isDark ? "#09090b" : "#ffffff";
    document.body.style.backgroundColor = target;
    document.documentElement.style.backgroundColor = target;
    return () => {
      document.body.style.backgroundColor = prevBody;
      document.documentElement.style.backgroundColor = prevHtml;
    };
  }, [menuOpen]);

  const onLogout = async () => {
    await logout();
    navigate("/dolgozo/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-zinc-50 dark:bg-zinc-950">
        <div className="text-[14px] text-zinc-500">{t("common.loading")}</div>
      </div>
    );
  }

  if (!employee) {
    return <Navigate to="/dolgozo/login" replace state={{ from: location.pathname }} />;
  }

  return (
    <div
      data-testid="portal-shell"
      className="relative min-h-screen bg-zinc-50 text-zinc-900 antialiased dark:bg-zinc-950 dark:text-zinc-50"
    >
      <UsePwaManifest href="/manifest-portal.json" title="SIPX Dolgozó" />
      {/* DESKTOP TOP BAR */}
      <header className="sticky top-0 z-40 hidden bg-zinc-50/85 backdrop-blur-xl dark:bg-zinc-950/85 md:block">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-6 px-6">
          {/* Brand */}
          <div className="flex items-center gap-2.5">
            <BrandMark size={32} rounded="xl" />
            <span className="text-[15px] font-semibold tracking-tight">SIPX</span>
          </div>

          {/* Center nav — full menu visible on desktop */}
          <nav className="flex items-center gap-1">
            {ALL_NAV.map((it) => {
              const Icon = it.icon;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  data-testid={it.testId.replace("menu-", "tab-")}
                  className={({ isActive }) =>
                    "inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-[12.5px] font-medium tracking-tight transition-colors " +
                    (isActive
                      ? "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400"
                      : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-900 dark:hover:text-white")
                  }
                >
                  <Icon className="h-4 w-4" strokeWidth={2} />
                  {t(it.labelKey)}
                </NavLink>
              );
            })}
          </nav>

          {/* User */}
          <div className="flex items-center gap-3">
            <div className="hidden text-right md:block">
              <p className="text-[13px] font-medium leading-tight">{employee.name}</p>
              <p className="text-[11.5px] text-zinc-500">{employee.position || "—"}</p>
            </div>
            <div className="grid h-9 w-9 place-items-center rounded-full bg-zinc-900 text-[12px] font-semibold text-white dark:bg-white dark:text-zinc-900">
              {initials(employee)}
            </div>
            <button
              type="button"
              onClick={onLogout}
              data-testid="portal-logout"
              className="grid h-9 w-9 place-items-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-900 dark:hover:text-white"
              aria-label={t("common.logout")}
            >
              <LogOut className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        </div>
      </header>

      {/* MAIN */}
      <main className="mx-auto w-full max-w-3xl pt-[env(safe-area-inset-top)] pb-[140px] md:max-w-5xl md:pt-0 md:pb-10">
        <Outlet />
      </main>

      {/* iOS SAFE-AREA FILLER (mobile only) — covers the iOS home-indicator zone with white
          so the body bg (zinc-50) does NOT bleed through and create a darker "csúnya szürke sáv"
          below the floating nav. Positioned PAST the viewport bottom (`bottom: -sai`) so it sits
          inside the iOS safe-area zone (where `bottom: 0` would still be inside the visible
          viewport). On Mac/Android (no safe area) it collapses to 0 and is invisible. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 z-30 bg-white dark:bg-zinc-900 md:hidden"
        style={{
          bottom: "calc(0px - env(safe-area-inset-bottom, 0px))",
          height: "env(safe-area-inset-bottom, 0px)",
        }}
      />

      {/* FLOATING BOTTOM NAV (mobile) — 4 tabs + hamburger.
          Container sits at bottom-0 of the viewport (guaranteed flush bottom on iOS PWA where
          env(safe-area-inset-bottom) used as `bottom` can misbehave). The pill is padded
          internally by safe-area-inset-bottom so it visually floats above the iOS home indicator. */}
      <nav
        data-testid="portal-bottom-nav"
        className="fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pt-2 pb-[max(env(safe-area-inset-bottom,0px),16px)] md:hidden"
      >
        <ul className="flex items-center gap-1 rounded-full bg-white/95 px-2 py-1.5 shadow-[0_8px_30px_-8px_rgba(0,0,0,0.16),0_2px_6px_-2px_rgba(0,0,0,0.08)] ring-1 ring-zinc-900/[0.04] backdrop-blur-xl dark:bg-zinc-900/95 dark:ring-white/[0.06]">
          {PRIMARY_TABS.map((it) => {
            const Icon = it.icon;
            return (
              <li key={it.to}>
                <NavLink
                  to={it.to}
                  end={it.end}
                  data-testid={it.testId}
                  className={({ isActive }) =>
                    "grid h-11 w-11 place-items-center rounded-full transition-colors duration-150 " +
                    (isActive
                      ? "bg-blue-500 text-white shadow-[0_4px_14px_-4px_rgba(59,130,246,0.5)] dark:bg-blue-500"
                      : "text-zinc-400 hover:text-zinc-900 dark:hover:text-white")
                  }
                  aria-label={t(it.labelKey)}
                >
                  <Icon className="h-[19px] w-[19px]" strokeWidth={2} />
                </NavLink>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              data-testid="portal-menu-open"
              aria-label={t("common.menu")}
              className="grid h-11 w-11 place-items-center rounded-full text-zinc-400 transition-colors duration-150 hover:text-zinc-900 dark:hover:text-white"
            >
              <Menu className="h-[19px] w-[19px]" strokeWidth={2} />
            </button>
          </li>
        </ul>
      </nav>

      {/* MENU DRAWER (mobile) — custom implementation (no Vaul) for predictable bottom alignment */}
      {menuOpen && (
        <>
          {/* Backdrop — only covers the WebView area; we leave the iOS bottom safe-area
              uncovered so the (now white) body bg blends with the drawer bg cleanly. */}
          <div
            className="fixed inset-x-0 top-0 z-50 bg-black/60 backdrop-blur-sm"
            style={{ bottom: "env(safe-area-inset-bottom, 0px)" }}
            onClick={() => setMenuOpen(false)}
            aria-hidden
          />
          {/* Sheet */}
          <div
            data-testid="portal-menu-drawer"
            role="dialog"
            aria-modal="true"
            className="fixed inset-x-0 z-50 overflow-hidden rounded-t-3xl bg-white shadow-[0_-8px_30px_-8px_rgba(0,0,0,0.18)] dark:bg-zinc-900"
            style={{
              bottom: "calc(0px - env(safe-area-inset-bottom))",
              height: "max-content",
              maxHeight: "88dvh",
              paddingBottom: 0,
            }}
          >
            {/* Drag handle */}
            <div className="mx-auto mt-3 h-1.5 w-10 rounded-full bg-zinc-300 dark:bg-zinc-700" />

            {/* Header */}
            <div className="flex items-center justify-between border-b border-zinc-100 px-5 pb-4 pt-3 dark:border-zinc-800">
              <h2 className="flex items-center gap-2 text-[16px] font-semibold tracking-tight">
                <BrandMark size={28} rounded="lg" />
                {t("common.menu")}
              </h2>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="grid h-9 w-9 place-items-center rounded-full text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800"
                aria-label={t("common.close")}
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>

            <div className="px-5 pb-[env(safe-area-inset-bottom)] pt-3">
              {/* Profile preview */}
              <div className="mb-5 flex items-center gap-3 rounded-2xl bg-zinc-50 px-4 py-3 dark:bg-zinc-800/40">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-[12px] font-semibold text-white">
                  {initials(employee)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold tracking-tight">{employee.name}</p>
                  <p className="truncate text-[12px] text-zinc-500">{employee.position || "—"}</p>
                </div>
              </div>

              {/* Grid menu */}
              <div className="grid grid-cols-3 gap-2.5">
                {ALL_NAV.map((it) => {
                  const Icon = it.icon;
                  const isActive =
                    it.end
                      ? location.pathname === it.to
                      : location.pathname.startsWith(it.to);
                  return (
                    <Link
                      key={it.to}
                      to={it.to}
                      data-testid={it.testId}
                      onClick={() => setMenuOpen(false)}
                      className={
                        "flex flex-col items-center justify-center gap-2 rounded-2xl px-2 py-4 transition-colors " +
                        (isActive
                          ? "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400"
                          : "bg-zinc-50 text-zinc-700 hover:bg-zinc-100 dark:bg-zinc-800/40 dark:text-zinc-300 dark:hover:bg-zinc-800")
                      }
                    >
                      <Icon className="h-5 w-5" strokeWidth={2} />
                      <span className="text-center text-[12px] font-medium tracking-tight">{t(it.labelKey)}</span>
                    </Link>
                  );
                })}
              </div>

              {/* Language switcher */}
              <div className="mt-5">
                <LanguageSwitcher />
              </div>

              {/* Logout */}
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onLogout();
                }}
                data-testid="portal-menu-logout"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-red-50 py-3 text-[13.5px] font-semibold text-red-600 transition hover:bg-red-100 dark:bg-red-950/30 dark:text-red-400 dark:hover:bg-red-950/50"
              >
                <LogOut className="h-4 w-4" strokeWidth={2} />
                {t("common.logout")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function initials(emp) {
  const src = (emp?.name || "").trim();
  if (!src) return "—";
  const parts = src.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
